<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("kol.menu.staff") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="handleCreate()">
            <i class="fa fa-plus"></i> {{ $t("add") }}
          </base-button>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-md-4">
                <base-input
                  v-model="searchQuery"
                  prepend-icon="fas fa-search"
                  :placeholder="$t('search')"
                >
                </base-input>
              </div>
            </div>
          </template>
          <div>
            <el-table
              :data="queriedData"
              row-key="id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('kol.staff_name')" min-width="200" prop="name" sortable>
                <div slot-scope="{ row }">
                  {{ row.name }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.nickname')" min-width="150">
                <div slot-scope="{ row }">
                  {{ row.nickname || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('status')" min-width="100" align="center">
                <div slot-scope="{ row }">
                  <badge :type="row.is_active ? 'success' : 'secondary'" class="badge-lg">
                    {{ row.is_active ? $t('enabled') : $t('disabled') }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('actions')" min-width="120" align="center">
                <div slot-scope="{ row }" class="d-flex justify-content-center">
                  <base-button
                    @click.native="handleEdit(row)"
                    class="edit"
                    type="warning"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-edit"></i>
                  </base-button>
                  <base-button
                    @click.native="handleDelete(row)"
                    class="remove btn-link"
                    type="danger"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-trash"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            v-if="queriedData.length != 0"
          >
            <div class="">
              <p class="card-category">
                {{ $t("data") }} {{ from + 1 }} {{ $t("to") }} {{ to }}
                {{ $t("of") }} {{ total }}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <modal :show.sync="showModal" modal-classes="modal-secondary">
      <h5 slot="header" class="modal-title">
        {{ isEdit ? $t('kol.edit_staff') : $t('kol.create_staff') }}
      </h5>
      <form class="new-event--form" @submit.prevent="">
        <base-input :label="$t('kol.staff_name')" v-model="formData.name" required>
        </base-input>

        <base-input :label="$t('kol.nickname')" v-model="formData.nickname">
        </base-input>

        <div class="form-group">
          <label class="form-control-label">{{ $t('status') }}</label>
          <div>
            <el-switch
              v-model="formData.is_active"
              active-color="#13ce66"
              inactive-color="#909399"
            >
            </el-switch>
            <span class="ml-2">{{ formData.is_active ? $t('enabled') : $t('disabled') }}</span>
          </div>
        </div>
      </form>

      <template slot="footer">
        <button type="submit" class="btn btn-md btn-primary" @click="handleSave()">
          {{ $t("save") }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Switch } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination, Badge } from "@/components";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    RouteBreadCrumb,
    Badge,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
  },
  computed: {
    ...mapGetters({
      tableData: "KOL/getStaff",
    }),
  },
  data() {
    return {
      propsToSearch: ["name", "nickname"],
      loading: false,
      showModal: false,
      isEdit: false,
      formData: {
        id: 0,
        name: "",
        nickname: "",
        is_active: true,
      },
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions({
      listStaff: "KOL/listStaff",
      createStaff: "KOL/createStaff",
      updateStaff: "KOL/updateStaff",
      deleteStaff: "KOL/deleteStaff",
    }),
    async loadData() {
      this.loading = true;
      try {
        await this.listStaff({});
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.isEdit = false;
      this.formData = {
        id: 0,
        name: "",
        nickname: "",
        is_active: true,
      };
      this.showModal = true;
    },
    handleEdit(row) {
      this.isEdit = true;
      this.formData = {
        id: row.id,
        name: row.name,
        nickname: row.nickname || "",
        is_active: row.is_active,
      };
      this.showModal = true;
    },
    handleSave() {
      var self = this;
      if (!this.formData.name) {
        swal.fire({
          title: this.$t("errors.invalid_param"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
        return;
      }

      const payload = {
        name: this.formData.name,
        nickname: this.formData.nickname,
        is_active: this.formData.is_active,
      };

      if (this.isEdit) {
        this.updateStaff({ id: this.formData.id, payload })
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("edit_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      } else {
        this.createStaff(payload)
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("create_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      }
    },
    handleDelete(row) {
      var self = this;
      swal
        .fire({
          title: this.$t("delete_confirm"),
          text: this.$t("delete_confirm_question") + row.name,
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "btn btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          buttonsStyling: false,
        })
        .then(function (result) {
          if (result.value) {
            self.deleteStaff(row.id)
              .then(function () {
                self.loadData();
                swal.fire({
                  title: self.$t("delete_success"),
                  icon: "success",
                  confirmButtonClass: "btn btn-success btn-fill",
                  buttonsStyling: false,
                });
              })
              .catch(function (err) {
                var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
                swal.fire({
                  title: self.$t("error"),
                  text: errorMsg,
                  icon: "error",
                  confirmButtonClass: "btn btn-danger btn-fill",
                  buttonsStyling: false,
                });
              });
          }
        });
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
.badge-lg {
  font-size: 0.8rem;
  padding: 0.5em 0.8em;
}
</style>
